import {
  AgenticSettingsEntityType,
  AgenticSettingsReviewSuggestionStatus,
  type AgenticSettingsCopyExamplesContentInput,
  type CopyReferencesReviewCopyExampleAdditionInput,
  type CopyReferencesReviewCopyExampleEditInput,
  type CopyReferencesReviewCopyExampleOverrideInput,
  type CopyReferencesReviewSuggestionFieldsFragment,
  type UpdateAgenticSettingsEntityInput,
} from "src/graphql/types";
import type { CopyExamplesContent } from "src/pages/context-hub/context-entities/types";
import {
  ALL_PROFILES_ID,
  copyExamplesContentToInput,
  liveBrandProfileIds,
} from "src/pages/context-hub/context-entities/utils";
import type { CopyReferencesRow } from "src/pages/context-hub/copy-references/table-utils";
import {
  CHANNEL_LABELS,
  isCopyReferenceChannel,
  type CopyReferenceChannel,
  type CopyReferencesEntity,
} from "src/pages/context-hub/copy-references/utils";

export type CopyExample = CopyExamplesContent["examples"][number];
export type ExtractionParams = CopyExamplesContent["extractionParams"];

// The copy-examples payload shared by live entities and review suggestions,
// dropping __typename (required on entities, optional on suggestions).
export type CopyExamplesContentLike = Omit<CopyExamplesContent, "__typename">;

export type ReviewSuggestion = CopyReferencesReviewSuggestionFieldsFragment;

// The Knowledge tab this detail page belongs to.
export const COPY_REFERENCES_TAB_PATH =
  "/context-hub/knowledge/copy-references";

// The detail page's own scope params. They differ from the list's filter params
// (`brandProfileId`, `status`), which the detail page carries too.
const SCOPE_PROFILE_PARAM = "profile";
const SCOPE_DESTINATION_PARAM = "destination";

// The detail page for one set of references. The channel alone doesn't identify
// a set — a workspace can scope references per brand profile and per ESP — so
// the rest of the tuple travels in the query string. `search` carries other
// params over, such as the list's filters.
export const copyReferencesDetailPath = (
  {
    channel,
    profileId,
    destinationId,
  }: {
    channel: CopyReferenceChannel;
    // Raw ids: null means all brand profiles / the channel-generic set.
    profileId: string | null;
    destinationId: string | null;
  },
  search = "",
): string => {
  const params = new URLSearchParams(search);
  params.delete(SCOPE_PROFILE_PARAM);
  params.delete(SCOPE_DESTINATION_PARAM);
  if (profileId) {
    params.set(SCOPE_PROFILE_PARAM, profileId);
  }

  if (destinationId) {
    params.set(SCOPE_DESTINATION_PARAM, destinationId);
  }

  const query = params.toString();
  return `${COPY_REFERENCES_TAB_PATH}/${channel}${query ? `?${query}` : ""}`;
};

// The set's brand profile and destination, as `copyReferencesDetailPath` wrote them.
export const readCopyReferencesScopeParams = (
  searchParams: URLSearchParams,
): { profileId: string | null; destinationId: string | null } => ({
  profileId: searchParams.get(SCOPE_PROFILE_PARAM) || null,
  destinationId: searchParams.get(SCOPE_DESTINATION_PARAM) || null,
});

// A set's title, e.g. "Email references".
export const copyReferencesHeading = (channel: CopyReferenceChannel): string =>
  `${CHANNEL_LABELS[channel]} references`;

// The detail rail searches what each row shows: its title, source, and brand profile.
export const matchesRailSearch = (
  row: Pick<CopyReferencesRow, "scope" | "sourceLabel" | "profileName">,
  search: string,
): boolean => {
  const query = search.trim().toLowerCase();
  if (!query) return true;
  return [
    copyReferencesHeading(row.scope.channel),
    row.sourceLabel,
    row.profileName,
  ].some((text) => text?.toLowerCase().includes(query));
};

// Mirrors COPY_EXAMPLES_MAX_SHORT_TEXT_LENGTH in
// packages/backend/core/content/lcm-copy-examples/types.ts (short-text example
// fields and the entity description share the cap).
export const SHORT_TEXT_MAX_LENGTH = 2_000;
export const DESCRIPTION_MAX_LENGTH = SHORT_TEXT_MAX_LENGTH;

// The example fields relevant to each channel, in display order. Shared by the
// review/live tables (as columns) and the example editor (as inputs). `body` is
// always present; other fields surface when populated (table) or always (editor).
export type ExampleField =
  | "campaignName"
  | "subject"
  | "preheader"
  | "title"
  | "cta"
  | "body";

export const FIELD_LABELS: Record<ExampleField, string> = {
  campaignName: "Campaign",
  subject: "Subject",
  preheader: "Preheader",
  title: "Title",
  cta: "CTA",
  body: "Body",
};

const CHANNEL_FIELDS: Record<CopyReferenceChannel, ExampleField[]> = {
  email: ["subject", "preheader", "body", "cta"],
  sms: ["campaignName", "body", "cta"],
  push: ["title", "body"],
  // Ads map headline -> title; card copy is folded into the body at ingestion.
  ads: ["title", "body", "cta"],
};

const DEFAULT_FIELDS: ExampleField[] = ["campaignName", "subject", "body"];

export const fieldsForChannel = (channel: string): ExampleField[] =>
  isCopyReferenceChannel(channel) ? CHANNEL_FIELDS[channel] : DEFAULT_FIELDS;

// How a channel's fields split into table columns: the short-text fields ahead
// of `body` collapse into one primary column (title line plus subtitles, e.g.
// email's "Subject and Preheader"), and each remaining field gets its own.
// `body` always has a column; the others appear only when populated.
export const exampleColumnFields = (
  channel: string,
  examples: Pick<CopyExample, ExampleField>[],
): { primary: ExampleField[]; rest: ExampleField[] } => {
  const fields = fieldsForChannel(channel).filter(
    (field) => field === "body" || examples.some((example) => example[field]),
  );
  const bodyIndex = fields.indexOf("body");
  return bodyIndex > 0
    ? { primary: fields.slice(0, bodyIndex), rest: fields.slice(bodyIndex) }
    : { primary: [], rest: fields };
};

export const primaryColumnLabel = (fields: ExampleField[]): string =>
  fields.map((field) => FIELD_LABELS[field]).join(" and ");

// Public permalink for an ad in the Meta Ad Library, keyed by the ad archive id
// we store as the example's sourceId.
export const metaAdLibraryAdUrl = (sourceId: string): string =>
  `https://www.facebook.com/ads/library/?id=${encodeURIComponent(sourceId)}`;

// e.g. "Last 90 days · ≥50 recipients"
export const configSummaryLabel = (
  params: ExtractionParams | null | undefined,
  channel?: string | null,
): string => {
  if (!params) return "";
  // Ads ingestion takes everything on the brand page; its stored window and
  // target are backend maximums, not user choices worth surfacing.
  if (channel === "ads") return "All available ads";
  const parts = [`Last ${params.lookbackDays} days`];
  if (params.minSends != null) {
    parts.push(`≥${params.minSends} recipients`);
  }
  return parts.join(" · ");
};

// The window a set's references came from, to follow their count, e.g.
// "from the last 90 days". Ads take everything on the brand page.
export const referencesWindowLabel = (
  params: ExtractionParams | null | undefined,
  channel: string,
): string | null => {
  if (!params) return null;
  if (channel === "ads") return "from all available ads";
  return `from the last ${params.lookbackDays} days`;
};

// Display title fallback chain for an example. `index` is the example's
// position in the unfiltered list, used for the "Example N" fallback.
export const exampleTitle = (
  example: Pick<CopyExample, "campaignName" | "subject" | "title">,
  index: number,
): string =>
  example.campaignName ??
  example.subject ??
  example.title ??
  `Example ${index + 1}`;

// The channel's first two populated short-text fields ahead of `body` title and
// subtitle the row; the body fills in for a missing second field.
export const examplePreview = (
  channel: string,
  example: Pick<CopyExample, ExampleField>,
  index: number,
): { title: string; subtitle: string } => {
  const fields = fieldsForChannel(channel);
  const bodyIndex = fields.indexOf("body");
  const populated = (bodyIndex > 0 ? fields.slice(0, bodyIndex) : [])
    .map((field) => example[field])
    .filter((value): value is string => Boolean(value));
  return {
    title: populated[0] ?? exampleTitle(example, index),
    subtitle: populated[1] ?? example.body,
  };
};

// The example to select after removing one from the browser: the next row in
// the sorted list order, falling back to the previous row, then to none.
export const nextExampleAfterRemoval = <
  T extends Pick<CopyExample, "id" | "sentAt" | "excluded">,
>(
  examples: T[],
  removedId: string,
  isExcluded?: (example: T) => boolean,
): T | null => {
  const sorted = sortExamples(examples, isExcluded);
  const index = sorted.findIndex((example) => example.id === removedId);
  if (index === -1) return null;
  return sorted[index + 1] ?? sorted[index - 1] ?? null;
};

export const filterExamples = <
  T extends Pick<
    CopyExample,
    "subject" | "preheader" | "title" | "cta" | "body" | "campaignName"
  >,
>(
  examples: T[],
  search: string,
): T[] => {
  const query = search.trim().toLowerCase();
  if (!query) return examples;
  return examples.filter((example) =>
    [
      example.subject,
      example.preheader,
      example.title,
      example.cta,
      example.body,
      example.campaignName,
    ].some((field) => field?.toLowerCase().includes(query)),
  );
};

// Newest-first, with active (included) examples above excluded ones so the set
// the agent uses stays at the top. Examples without a sent date sort last.
//
// `isExcluded` defaults to the stored flag, but callers can pass an override-aware
// predicate (e.g. the review flow, where include/exclude edits live in local
// state) so the ordering tracks the reviewer's pending changes, not just the
// proposed flags.
export const sortExamples = <
  T extends Pick<CopyExample, "sentAt" | "excluded">,
>(
  examples: T[],
  isExcluded: (example: T) => boolean = (example) => example.excluded,
): T[] => {
  const byNewest = (a: T, b: T): number => {
    if (a.sentAt == null && b.sentAt == null) return 0;
    if (a.sentAt == null) return 1;
    if (b.sentAt == null) return -1;
    return Date.parse(b.sentAt) - Date.parse(a.sentAt);
  };

  return [...examples].sort((a, b) => {
    const aExcluded = isExcluded(a);
    const bExcluded = isExcluded(b);
    if (aExcluded !== bExcluded) return aExcluded ? 1 : -1;
    return byNewest(a, b);
  });
};

// The pending copy-examples suggestion for the given channel and destination, if
// any. Brand-profile scope is enforced by the run that owns the review, since
// suggestions carry no assignments.
export const copyExamplesSuggestionForChannel = <
  T extends Pick<ReviewSuggestion, "proposedContent" | "status">,
>(
  suggestions: T[],
  channel: string,
  destinationId: string | null,
): { suggestion: T; content: CopyExamplesContentLike } | null => {
  for (const suggestion of suggestions) {
    if (suggestion.status !== AgenticSettingsReviewSuggestionStatus.Pending) {
      continue;
    }
    const content = suggestion.proposedContent;
    // A generic suggestion has no target, so `destinationId` is undefined there;
    // normalize to null to match a generic (null-destination) scope. Mirrors
    // configMatchesDestination.
    if (
      content.channel === channel &&
      (content.target?.destinationId ?? null) === destinationId
    ) {
      return { suggestion, content };
    }
  }
  return null;
};

// Overrides to send with an accept decision: only the examples whose
// include/exclude state the reviewer flipped from what was proposed.
export const diffExampleOverrides = (
  proposedExamples: Pick<CopyExample, "id" | "excluded">[],
  overrides: Record<string, boolean>,
): CopyReferencesReviewCopyExampleOverrideInput[] =>
  proposedExamples
    .filter((example) => {
      const override = overrides[example.id];
      return override !== undefined && override !== example.excluded;
    })
    .map((example) => ({
      exampleId: example.id,
      excluded: overrides[example.id] ?? example.excluded,
    }));

// Full update-entity input that swaps in new copy-examples content while
// preserving the entity's name/description/active state.
export const buildCopyReferencesUpdateInput = (
  entity: CopyReferencesEntity,
  content: AgenticSettingsCopyExamplesContentInput,
): UpdateAgenticSettingsEntityInput => ({
  id: entity.id,
  name: entity.name,
  description: entity.description ?? null,
  isActive: entity.isActive,
  type: AgenticSettingsEntityType.CopyReferences,
  contextCopyExamples: content,
});

// Update-entity input that changes only the entity description (surfaced to the
// copy-writing agent as guidance) while preserving the existing copy-examples content.
export const buildCopyReferencesDescriptionUpdateInput = (
  entity: CopyReferencesEntity,
  description: string,
): UpdateAgenticSettingsEntityInput => ({
  ...buildCopyReferencesUpdateInput(
    entity,
    copyExamplesContentToInput(entity.content),
  ),
  description: description.trim() ? description : null,
});

// A single example's include/exclude change (true = excluded).
export type ExampleInclusionChange = { id: string; excluded: boolean };

// Editor working copy of an example's copy fields. All fields are plain strings
// (empty = absent) so the editor inputs stay controlled; trimming and
// empty-to-null conversion happen when a draft is converted to an input.
export type CopyExampleDraft = {
  campaignName: string;
  subject: string;
  preheader: string;
  title: string;
  cta: string;
  body: string;
};

export const emptyExampleDraft = (): CopyExampleDraft => ({
  campaignName: "",
  subject: "",
  preheader: "",
  title: "",
  cta: "",
  body: "",
});

// Every copy field is optional, but an example needs copy in at least one of its
// channel's fields. The campaign name labels an example; it is not copy.
export const exampleDraftHasCopy = (
  channel: string,
  draft: CopyExampleDraft,
): boolean =>
  fieldsForChannel(channel).some(
    (field) => field !== "campaignName" && draft[field].trim().length > 0,
  );

export const EXAMPLE_NEEDS_COPY_MESSAGE =
  "An example needs copy in at least one field.";

export const draftFromExample = (example: CopyExample): CopyExampleDraft => ({
  campaignName: example.campaignName ?? "",
  subject: example.subject ?? "",
  preheader: example.preheader ?? "",
  title: example.title ?? "",
  cta: example.cta ?? "",
  body: example.body,
});

// Overlays a draft's fields onto an example for display while the reviewer's
// edits are still local (pre-approval).
export const exampleWithDraft = (
  example: CopyExample,
  draft: CopyExampleDraft,
): CopyExample => ({
  ...example,
  campaignName: draft.campaignName.trim() || null,
  subject: draft.subject.trim() || null,
  preheader: draft.preheader.trim() || null,
  title: draft.title.trim() || null,
  cta: draft.cta.trim() || null,
  body: draft.body.trim(),
});

// Edit input for one example, containing only the fields the reviewer actually
// changed from what was proposed (empty string = clear the field). Null when
// the draft matches the proposal.
export const diffExampleEdit = (
  proposed: CopyExample,
  draft: CopyExampleDraft,
): CopyReferencesReviewCopyExampleEditInput | null => {
  const edit: CopyReferencesReviewCopyExampleEditInput = {
    exampleId: proposed.id,
  };
  let changed = false;
  const optionalFields = [
    "campaignName",
    "subject",
    "preheader",
    "title",
    "cta",
  ] as const;
  for (const field of optionalFields) {
    const value = draft[field].trim();
    if (value !== (proposed[field] ?? "")) {
      edit[field] = value;
      changed = true;
    }
  }
  const body = draft.body.trim();
  if (body !== proposed.body) {
    edit.body = body;
    changed = true;
  }
  return changed ? edit : null;
};

export const additionInputFromDraft = (
  draft: CopyExampleDraft,
): CopyReferencesReviewCopyExampleAdditionInput => ({
  campaignName: draft.campaignName.trim() || null,
  subject: draft.subject.trim() || null,
  preheader: draft.preheader.trim() || null,
  title: draft.title.trim() || null,
  cta: draft.cta.trim() || null,
  body: draft.body.trim(),
});

// Display-only stand-in for a reviewer-authored example that hasn't been
// persisted yet. `id` is a local id used to key list rows and edits.
export const exampleFromAdditionDraft = (
  id: string,
  draft: CopyExampleDraft,
): CopyExample => ({
  id,
  sourceId: null,
  campaignName: draft.campaignName.trim() || null,
  subject: draft.subject.trim() || null,
  preheader: draft.preheader.trim() || null,
  title: draft.title.trim() || null,
  cta: draft.cta.trim() || null,
  body: draft.body.trim(),
  rawBody: null,
  sentAt: null,
  excluded: false,
  exclusionReason: null,
  imageUrls: null,
});

// Excluding an example is reversible: it stays in the content with `excluded`
// flipped rather than being dropped, so a later edit can re-include it (set
// `excluded: false`) without re-ingesting. Only the flag changes here.
export const contentInputWithExamplesExcluded = (
  content: CopyExamplesContent,
  changes: ExampleInclusionChange[],
): AgenticSettingsCopyExamplesContentInput => {
  const excludedById = new Map(
    changes.map((change) => [change.id, change.excluded]),
  );
  const input = copyExamplesContentToInput(content);
  return {
    ...input,
    examples: input.examples.map((example) =>
      excludedById.has(example.id)
        ? {
            ...example,
            excluded: excludedById.get(example.id) ?? example.excluded,
          }
        : example,
    ),
  };
};

// Full-snapshot content input with one example's copy fields replaced by the
// draft. When the body actually changes, the pre-edit copy is kept in rawBody
// for audit (an existing rawBody wins — it is the earlier original).
export const contentInputWithExampleEdited = (
  content: CopyExamplesContent,
  exampleId: string,
  draft: CopyExampleDraft,
): AgenticSettingsCopyExamplesContentInput => {
  const input = copyExamplesContentToInput(content);
  return {
    ...input,
    examples: input.examples.map((example) => {
      if (example.id !== exampleId) return example;
      const body = draft.body.trim();
      return {
        ...example,
        campaignName: draft.campaignName.trim() || null,
        subject: draft.subject.trim() || null,
        preheader: draft.preheader.trim() || null,
        title: draft.title.trim() || null,
        cta: draft.cta.trim() || null,
        body,
        rawBody:
          body !== example.body
            ? (example.rawBody ?? example.body)
            : example.rawBody,
      };
    }),
  };
};

export const contentInputWithExampleAdded = (
  content: CopyExamplesContent,
  draft: CopyExampleDraft,
): AgenticSettingsCopyExamplesContentInput => {
  const input = copyExamplesContentToInput(content);
  return {
    ...input,
    examples: [
      ...input.examples,
      {
        id: crypto.randomUUID(),
        sourceId: null,
        campaignName: draft.campaignName.trim() || null,
        subject: draft.subject.trim() || null,
        preheader: draft.preheader.trim() || null,
        title: draft.title.trim() || null,
        cta: draft.cta.trim() || null,
        body: draft.body.trim(),
        rawBody: null,
        sentAt: null,
        excluded: false,
        exclusionReason: null,
        imageUrls: null,
      },
    ],
  };
};

// Unlike excluding (contentInputWithExamplesExcluded), removing drops the
// example from the stored content entirely — it cannot be re-included later.
export const contentInputWithExampleRemoved = (
  content: CopyExamplesContent,
  exampleId: string,
): AgenticSettingsCopyExamplesContentInput => {
  const input = copyExamplesContentToInput(content);
  return {
    ...input,
    examples: input.examples.filter((example) => example.id !== exampleId),
  };
};

// Copy-references sets own at most one brand assignment, so the selector maps a
// single option value to the assignment list the mutation expects: the
// "All brand profiles" sentinel clears assignments, a specific profile scopes
// to exactly that profile.
export const brandProfileIdsForSelection = (value: string): string[] =>
  value === ALL_PROFILES_ID ? [] : [value];

// The currently selected option value for a set: its single live assignment, or
// the all-profiles sentinel when it has none.
export const selectedBrandProfileValue = (
  entity: CopyReferencesEntity,
): string => liveBrandProfileIds(entity)[0] ?? ALL_PROFILES_ID;
